import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import WorkItemOpen from "./WorkItemOpen";
import CustomLoader from "../static/CustomLoader";
import {
  MasterPage,
  MasterToolbar,
  MasterButton,
  MasterGrid,
  MasterDialog,
  MasterConfirmDialog,
} from "../static/Masterpage";

const MyAssignment = ({ refresh, setRefresh }) => {
  const apiUrl = localStorage.getItem("apiUrl");
  const dbTokenData = localStorage.getItem("dbTokenData");
  const headers = {
    Authorization: `Bearer ${dbTokenData}`,
    "Content-Type": "application/json",
    dbMaster: "",
  };

  const [density, setDensity] = useState("standard");
  const [myWorkItemData, setmyWorkItemData] = useState([{}]);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editShow, setEditShow] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [assignedUserOptions, setAssignedUserOptions] = useState([]);

  const editHandleClose = () => {
    setIsConfirmOpen(true);
  };

  const handleClose = () => {
    setEditShow(false);
  };

  const handleConfirmClose = () => {
    setIsConfirmOpen(false);
    setEditShow(false);
  };

  const editHandleShow = () => {
    if (
      selectedRowData == null ||
      selectedRowData.length > 1 ||
      selectedRowData.length === 0
    ) {
      toast.warning("Please select only one value");
      return;
    }
    setEditShow(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return format(new Date(dateString), "yyyy/MM/dd HH:mm:ss");
  };

  const getUserLabel = (assignedUserOptions, assigneCode) => {
    const assignee = assignedUserOptions.find(
      (option) => option.value === assigneCode,
    );
    const label = assignee ? assignee.label : assigneCode;
    return String(label);
  };

  const columns = [
    {
      field: "workid",
      headerName: "Permit No",
      width: 180,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <span
          style={{
            fontWeight: 700,
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "statusval",
      headerName: "Status",
      width: 130,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "previoususer",
      headerName: "Previous User",
      width: 180,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => getUserLabel(assignedUserOptions, params.value),
    },
    {
      field: "createdatetime",
      headerName: "Created Date Time",
      width: 210,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "lastupdatedatetime",
      headerName: "Last Update Date Time",
      width: 210,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "expectedcompletiontime",
      headerName: "Expected Resolution Date Time",
      width: 240,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "assignedtime",
      headerName: "Assigned Time",
      width: 210,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => formatDate(params.value),
    },
  ];

  useEffect(() => {
    fetchWorkItemData();
    fetchUserOptions();
  }, [refresh]);

  const handleRowSelection = (selectedIds) => {
    const selectedRow = myWorkItemData.find(
      (row) => row.wlid === selectedIds[0],
    );
    setSelectedRowData(selectedRow || null);
  };

  const fetchUserOptions = async () => {
    try {
      const response = await axios.get(`${apiUrl}user/activeuser`, { headers });

      if (Array.isArray(response.data)) {
        const options = response.data.map((user) => ({
          value: user.empcode,
          label: user.userid,
        }));

        setAssignedUserOptions(options);
      } else {
        console.error("Unexpected response format.");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchWorkItemData = async () => {
    try {
      const savedUser = localStorage.getItem("user");

      if (!savedUser) {
        console.error("No user found in local storage");
        return;
      }

      const parsedUser = JSON.parse(savedUser);

      if (!parsedUser.empcode) {
        console.error("Parsed user does not have 'empcode'");
        return;
      }

      setLoading(true);

      const requestHeaders = {
        Authorization: `Bearer ${dbTokenData}`,
        "Content-Type": "application/json",
        dbMaster: "",
        userId: parsedUser.empcode,
      };

      const response = await axios.get(`${apiUrl}worklist/myAssignments`, {
        headers: requestHeaders,
      });

      if (response.data.cd === 1) {
        return;
      }

      setmyWorkItemData(response.data);
    } catch (error) {
      console.error("Error fetching work item data:", error);
      toast.error("Failed to fetch work item data.");
    } finally {
      setLoading(false);
    }
  };

  const getOpenStatus = (params) => {
    const currentDate = new Date();

    const expectedDate = new Date(params.row.expectedcomdatetime);

    let workType = "";

    try {
      const commentData = JSON.parse(params.row.comment);

      if (commentData && commentData[0] && commentData[0].workType) {
        workType = commentData[0].workType;
      }
    } catch (error) {
      console.error("Error parsing comment data:", error);
    }

    const diffInMinutes = (expectedDate - currentDate) / (1000 * 60);

    if (params.row.wlid === selectedRowData?.wlid) {
      return "highlight-selected";
    }

    if (workType === "hotWork") {
      return "highlight-row-red";
    }

    if (workType === "coldWork") {
      return "highlight-row-blue";
    }

    if (diffInMinutes > 10) {
      return "highlight-row-new";
    }

    if (diffInMinutes <= 10 && diffInMinutes > 0) {
      return "highlight-row-less";
    }

    return "highlight-row-more";
  };

  return (
    <>
      <MasterPage>
        <MasterToolbar
          actions={
            <>
              <MasterButton
                variant="outline"
                tooltip="Refresh My Assignments"
                onClick={fetchWorkItemData}
                disabled={loading}
              >
                Refresh
              </MasterButton>

              <MasterButton
                variant="primary"
                tooltip="Open Selected Work Request"
                onClick={editHandleShow}
                disabled={loading}
              >
                Open
              </MasterButton>
            </>
          }
        />

        <MasterGrid
          density={density}
          onDensityChange={(newDensityString) => setDensity(newDensityString)}
          rows={
            myWorkItemData &&
            myWorkItemData.length > 0 &&
            Object.keys(myWorkItemData[0]).length !== 0
              ? myWorkItemData
              : []
          }
          columns={columns}
          onSelectionModelChange={handleRowSelection}
          getRowId={(row) => row.wlid}
          getRowClassName={getOpenStatus}
          sx={{
            "& .highlight-row-red": {
              backgroundColor: "#FF3B30",
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: "#F52F24",
              },
              "& .MuiDataGrid-cell": {
                color: "#FFFFFF",
                borderColor: "rgba(255,255,255,0.45)",
              },
            },
            "& .highlight-row-blue": {
              backgroundColor: "#087FF5",
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: "#0873DE",
              },
              "& .MuiDataGrid-cell": {
                color: "#FFFFFF",
                borderColor: "rgba(255,255,255,0.45)",
              },
            },
            "& .highlight-selected": {
              backgroundColor: "#E7F0FA",
              "&:hover": {
                backgroundColor: "#DCEAF7",
              },
              "& .MuiDataGrid-cell": {
                color: "#123B5D",
              },
            },
            "& .highlight-row-new": {
              backgroundColor: "#F2FBF6",
              "&:hover": {
                backgroundColor: "#E8F7EF",
              },
            },
            "& .highlight-row-less": {
              backgroundColor: "#FFF9E8",
              "&:hover": {
                backgroundColor: "#FFF3D1",
              },
            },
            "& .highlight-row-more": {
              backgroundColor: "#FFF0F3",
              "&:hover": {
                backgroundColor: "#FFE5EA",
              },
            },
          }}
        />
      </MasterPage>

      {loading && <CustomLoader />}

      <MasterDialog
        open={editShow}
        onHide={editHandleClose}
        title="Edit Work Request"
        maxWidth="xl"
      >
        <WorkItemOpen
          selectedId={selectedRowData?.workid}
          selectedStep={selectedRowData?.stepcd}
          selectedWorklistId={selectedRowData?.wlid}
          selectedWorklistPUser={selectedRowData?.previoususer}
          onHide={handleClose}
          setRefresh={setRefresh}
          mode="O"
          fetchWorkItemData={fetchWorkItemData}
        />
      </MasterDialog>

      <MasterConfirmDialog
        show={isConfirmOpen}
        title="Close Confirmation"
        message="Are you sure you want to close?"
        onConfirm={handleConfirmClose}
        onCancel={() => setIsConfirmOpen(false)}
      />
    </>
  );
};

export default MyAssignment;
